/**
 * src/composables/useDeviceEnvironment.js
 * Reactive environment detection: PWA Standalone, Mobile screen/UA, and PWA Installation Prompt
 */
import { ref, computed, onMounted, onUnmounted } from 'vue'

const isPwa = ref(false)
const isMobile = ref(false)
const windowWidth = ref(typeof window !== 'undefined' ? window.innerWidth : 1200)
const windowHeight = ref(typeof window !== 'undefined' ? window.innerHeight : 800)
const deferredInstallPrompt = ref(null)
const isInstallable = ref(false)
const isInstalled = ref(false)

let initialized = false

function checkEnvironment() {
  if (typeof window === 'undefined') return
  windowWidth.value = window.innerWidth
  windowHeight.value = window.innerHeight

  // Check PWA mode
  const standaloneMedia = window.matchMedia('(display-mode: standalone)').matches
  const iosStandalone = window.navigator.standalone === true
  const androidAppReferrer = document.referrer?.includes('android-app://')
  isPwa.value = Boolean(standaloneMedia || iosStandalone || androidAppReferrer)

  // Check Mobile
  const userAgentMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent)
  const smallScreen = window.innerWidth <= 768
  isMobile.value = Boolean(userAgentMobile || smallScreen)
}

function handleBeforeInstallPrompt(e) {
  e.preventDefault()
  deferredInstallPrompt.value = e
  isInstallable.value = true
}

function handleAppInstalled() {
  deferredInstallPrompt.value = null
  isInstallable.value = false
  isInstalled.value = true
  isPwa.value = true
}

export function useDeviceEnvironment() {
  onMounted(() => {
    checkEnvironment()
    if (!initialized && typeof window !== 'undefined') {
      initialized = true
      window.addEventListener('resize', checkEnvironment, { passive: true })
      window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt)
      window.addEventListener('appinstalled', handleAppInstalled)
    }
  })

  async function promptPwaInstall() {
    if (!deferredInstallPrompt.value) return false
    deferredInstallPrompt.value.prompt()
    const choice = await deferredInstallPrompt.value.userChoice
    if (choice.outcome === 'accepted') {
      deferredInstallPrompt.value = null
      isInstallable.value = false
      return true
    }
    return false
  }

  return {
    isPwa: computed(() => isPwa.value),
    isMobile: computed(() => isMobile.value),
    isInstallable: computed(() => isInstallable.value),
    isInstalled: computed(() => isInstalled.value),
    windowWidth: computed(() => windowWidth.value),
    windowHeight: computed(() => windowHeight.value),
    promptPwaInstall
  }
}
